import React, { useEffect, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  ListSubheader,
  MenuItem,
  Slider,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import LockRounded from "@mui/icons-material/LockRounded";
import LockOpenRounded from "@mui/icons-material/LockOpenRounded";
import VisibilityRounded from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRounded from "@mui/icons-material/VisibilityOffRounded";
import DeleteRounded from "@mui/icons-material/DeleteRounded";
import FormatBoldRounded from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRounded from "@mui/icons-material/FormatItalicRounded";
import FormatAlignLeftRounded from "@mui/icons-material/FormatAlignLeftRounded";
import FormatAlignCenterRounded from "@mui/icons-material/FormatAlignCenterRounded";
import FormatAlignRightRounded from "@mui/icons-material/FormatAlignRightRounded";
import VerticalAlignTopRounded from "@mui/icons-material/VerticalAlignTopRounded";
import VerticalAlignCenterRounded from "@mui/icons-material/VerticalAlignCenterRounded";
import VerticalAlignBottomRounded from "@mui/icons-material/VerticalAlignBottomRounded";
import AddPhotoAlternateRounded from "@mui/icons-material/AddPhotoAlternateRounded";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";
import {
  BLOCK_DEFS,
  ELEMENT_TYPES,
  FIELD_DEFS,
  FIELD_GROUPS,
  PALETTE,
  elementTitle,
  minSizeFor,
  round1,
} from "./canvasModel";

const BLUE = "#2563EB";
const BLUE_PALE = "#EFF6FF";
const BORDER = "#E2E8F0";
const INK = "#0F172A";
const INK_MUTED = "#64748B";

const microLabelSx = {
  fontSize: "0.62rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: INK_MUTED,
  mb: 0.75,
};
const groupSx = { border: `1px solid ${BORDER}`, borderRadius: 2, p: 1.25, mb: 1.25, bgcolor: "#fff" };
const smallField = { "& .MuiInputBase-input": { fontSize: "0.78rem", py: 0.6 }, "& .MuiInputLabel-root": { fontSize: "0.75rem" } };

const TEXT_TYPES = new Set(["field", "text", "block", "copyLabel"]);
const FONT_FAMILIES = [
  { value: "", label: "Default (Roboto)" },
  { value: "Arial, sans-serif", label: "Arial" },
  { value: "'Times New Roman', serif", label: "Times New Roman" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Courier New', monospace", label: "Courier New" },
  { value: "'Noto Sans Devanagari', 'Mangal', sans-serif", label: "Devanagari (Hindi)" },
  { value: "'Noto Sans Tamil', 'Latha', sans-serif", label: "Tamil" },
  { value: "'Noto Sans Telugu', 'Gautami', sans-serif", label: "Telugu" },
  { value: "'Noto Sans Kannada', 'Tunga', sans-serif", label: "Kannada" },
];

// Downscale an uploaded picture so a logo/stamp doesn't bloat the template JSON.
const readImage = (file, maxPx = 900) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const k = Math.min(1, maxPx / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * k);
        c.height = Math.round(img.height * k);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        // PNG keeps transparency (logos, stamps); fall back to JPEG if huge.
        let url = c.toDataURL("image/png");
        if (url.length > 450000) url = c.toDataURL("image/jpeg", 0.85);
        resolve({ url, ratio: img.width / img.height });
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

const NumField = ({ label, value, onChange, step = 0.5, min, max, suffix }) => (
  <TextField
    label={label}
    type="number"
    size="small"
    value={value}
    onChange={(e) => {
      const v = parseFloat(e.target.value);
      if (Number.isFinite(v)) onChange(min != null ? Math.max(min, max != null ? Math.min(max, v) : v) : v);
    }}
    inputProps={{ step, min, max }}
    InputProps={suffix ? { endAdornment: <Typography sx={{ fontSize: "0.65rem", color: INK_MUTED }}>{suffix}</Typography> } : undefined}
    sx={smallField}
    fullWidth
  />
);

const ColorField = ({ label, value, onChange, allowNone }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    <Box
      component="input"
      type="color"
      value={value || "#ffffff"}
      onChange={(e) => onChange(e.target.value)}
      sx={{ width: 30, height: 26, p: 0, border: `1px solid ${BORDER}`, borderRadius: 1, bgcolor: "transparent", cursor: "pointer" }}
    />
    <Typography sx={{ fontSize: "0.72rem", color: INK_MUTED, flex: 1 }}>{label}</Typography>
    {allowNone && value && (
      <Typography onClick={() => onChange("")} sx={{ fontSize: "0.65rem", color: BLUE, cursor: "pointer" }}>
        None
      </Typography>
    )}
  </Box>
);

// ─── Palette ────────────────────────────────────────────────────────────────
// Click a chip to drop the element in the middle of the page, or drag it onto
// the sheet to place it where it lands.
export const CanvasPalette = ({ onAdd, isReceipt }) => {
  const fieldGroups = FIELD_GROUPS.filter((g) => (isReceipt ? g !== "Payment" : g !== "Receipt"));
  const chip = (item, key) => (
    <Chip
      key={key}
      label={item.label}
      size="small"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("application/x-ticket-element", JSON.stringify({ type: item.type, opts: item.opts || {} }));
        e.dataTransfer.effectAllowed = "copy";
      }}
      onClick={() => onAdd(item.type, item.opts || {})}
      icon={<DragIndicatorRounded sx={{ fontSize: 14 }} />}
      sx={{
        fontSize: "0.7rem",
        height: 24,
        bgcolor: "#fff",
        border: `1px solid ${BORDER}`,
        cursor: "grab",
        "&:hover": { borderColor: BLUE, bgcolor: BLUE_PALE },
      }}
    />
  );
  return (
    <Box>
      {PALETTE.map((g) => (
        <Box key={g.group} sx={{ mb: 1.25 }}>
          <Typography sx={microLabelSx}>{g.group}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{g.items.map((it, i) => chip(it, `${g.group}-${i}`))}</Box>
        </Box>
      ))}
      <Typography sx={microLabelSx}>Data Fields</Typography>
      {fieldGroups.map((g) => (
        <Box key={g} sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: "0.65rem", color: INK, fontWeight: 600, mb: 0.5 }}>{g}</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {FIELD_DEFS.filter((f) => f.group === g).map((f) => chip({ type: "field", label: f.label, opts: { key: f.key } }, f.key))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// ─── Elements, front & back ─────────────────────────────────────────────────
// Every element of the ticket in one list, grouped by the face of the sheet it
// prints on — the free-form twin of the Sections card. The Front / Back chip
// flips an element to the other face (several at once when they are selected
// together), and clicking a row selects it and turns the editor to its face.
// Top-most first within a face; it is also the way to reach an element that is
// hidden or buried under another one.
export const CanvasLayers = ({ elements, selectedIds, onSelect, onUpdate, onMoveToSide, onShowSide, unplaced = [], onAddBlock }) => {
  const bySide = (side) =>
    elements.filter((el) => (el.side === "back") === (side === "back")).sort((a, b) => (b.z || 0) - (a.z || 0));
  const sideChip = (el) => {
    const back = el.side === "back";
    return (
      <Tooltip title={back ? "Prints on the back — click to move to the front" : "Prints on the front — click to move to the back"}>
        <Chip
          size="small"
          label={back ? "Back" : "Front"}
          onClick={(e) => {
            e.stopPropagation();
            // Moving a selected element moves the whole selection with it.
            const ids = selectedIds.includes(el.id) ? selectedIds : [el.id];
            onMoveToSide(ids, back ? "front" : "back");
          }}
          sx={{
            height: 20,
            fontSize: "0.62rem",
            fontWeight: 700,
            cursor: "pointer",
            bgcolor: back ? BLUE : "#fff",
            color: back ? "#fff" : INK_MUTED,
            border: `1px solid ${back ? BLUE : BORDER}`,
            "&:hover": { bgcolor: back ? "#1D4ED8" : BLUE_PALE },
          }}
        />
      </Tooltip>
    );
  };
  const row = (el) => {
    const sel = selectedIds.includes(el.id);
    return (
      <Box
        key={el.id}
        onClick={(e) => {
          onSelect(e.shiftKey ? (sel ? selectedIds.filter((x) => x !== el.id) : [...selectedIds, el.id]) : [el.id]);
          onShowSide && onShowSide(el.side === "back" ? "back" : "front");
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          cursor: "pointer",
          bgcolor: sel ? BLUE_PALE : "transparent",
          "&:hover": { bgcolor: sel ? BLUE_PALE : "#F8FAFC" },
        }}
      >
        <Typography noWrap sx={{ flex: 1, fontSize: "0.74rem", color: el.hidden ? INK_MUTED : INK, fontWeight: sel ? 700 : 400 }}>
          {elementTitle(el)}
          <Typography component="span" sx={{ fontSize: "0.62rem", color: INK_MUTED, ml: 0.75 }}>
            {ELEMENT_TYPES[el.type]?.label}
          </Typography>
        </Typography>
        {sideChip(el)}
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onUpdate([el.id], (x) => ({ ...x, hidden: !x.hidden }));
          }}
        >
          {el.hidden ? <VisibilityOffRounded sx={{ fontSize: 15 }} /> : <VisibilityRounded sx={{ fontSize: 15, color: INK_MUTED }} />}
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onUpdate([el.id], (x) => ({ ...x, locked: !x.locked }));
          }}
        >
          {el.locked ? <LockRounded sx={{ fontSize: 15, color: BLUE }} /> : <LockOpenRounded sx={{ fontSize: 15, color: INK_MUTED }} />}
        </IconButton>
      </Box>
    );
  };
  const group = (side) => {
    const rows = bySide(side);
    return (
      <Box sx={{ mb: side === "front" ? 1.25 : 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography sx={{ ...microLabelSx, mb: 0, color: side === "back" ? BLUE : INK_MUTED }}>
            {side === "back" ? "Back of sheet" : "Front of sheet"}
          </Typography>
          <Box sx={{ flexGrow: 1, height: "1px", bgcolor: BORDER }} />
          <Typography sx={{ fontSize: "0.6rem", color: INK_MUTED }}>{rows.length}</Typography>
        </Box>
        {rows.length ? (
          rows.map(row)
        ) : (
          <Typography sx={{ fontSize: "0.7rem", color: INK_MUTED, px: 0.75, py: 0.5 }}>
            {side === "back"
              ? "Nothing on the back. Tap an element's Front chip to send it here."
              : "Nothing on the front."}
          </Typography>
        )}
      </Box>
    );
  };
  // Sections that are not on the ticket at all — like a switched-off row in
  // the flow Sections card. "+ Front" / "+ Back" places it on that face.
  const addChip = (section, side) => (
    <Chip
      size="small"
      label={side === "back" ? "+ Back" : "+ Front"}
      onClick={() => onAddBlock(section, side)}
      sx={{
        height: 20,
        fontSize: "0.62rem",
        fontWeight: 700,
        cursor: "pointer",
        bgcolor: "#fff",
        color: BLUE,
        border: `1px solid ${BORDER}`,
        "&:hover": { bgcolor: BLUE_PALE, borderColor: BLUE },
      }}
    />
  );
  return (
    <Box>
      <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED, mb: 1 }}>
        Tap <b>Front</b> / <b>Back</b> to choose which side of the sheet an element prints on. Shift-click rows to move several together.
      </Typography>
      <Box sx={{ maxHeight: 460, overflowY: "auto", mx: -0.5 }}>
        {group("front")}
        {group("back")}
        {unplaced.length > 0 && onAddBlock && (
          <Box sx={{ mt: 1.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Typography sx={{ ...microLabelSx, mb: 0 }}>Not on the ticket</Typography>
              <Box sx={{ flexGrow: 1, height: "1px", bgcolor: BORDER }} />
              <Typography sx={{ fontSize: "0.6rem", color: INK_MUTED }}>{unplaced.length}</Typography>
            </Box>
            {unplaced.map((b) => (
              <Box key={b.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.25 }}>
                <Typography noWrap sx={{ flex: 1, fontSize: "0.74rem", color: INK_MUTED }}>{b.label}</Typography>
                {addChip(b.id, "front")}
                {addChip(b.id, "back")}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Properties ─────────────────────────────────────────────────────────────
export const CanvasProperties = ({ selected, onUpdate, onDelete, focusTextTick, onMoveToSide }) => {
  const textRef = useRef(null);
  useEffect(() => {
    if (focusTextTick && textRef.current) {
      textRef.current.focus();
      textRef.current.select?.();
    }
  }, [focusTextTick]);

  if (!selected.length) {
    return (
      <Typography sx={{ fontSize: "0.74rem", color: INK_MUTED }}>
        Select an element on the sheet to change its size, position, font and look. Shift-click or drag a box around
        several to change them together.
      </Typography>
    );
  }
  const ids = selected.map((el) => el.id);
  const el = selected[0];
  const single = selected.length === 1;
  const all = (fn) => selected.every(fn);
  const setStyle = (k, v, merge) => onUpdate(ids, (x) => ({ ...x, style: { ...x.style, [k]: v } }), merge && `style-${k}-${ids.join()}`);
  const setProp = (k, v, merge) => onUpdate(ids, (x) => ({ ...x, props: { ...x.props, [k]: v } }), merge && `prop-${k}-${ids.join()}`);
  const setGeo = (k, v) =>
    onUpdate(
      ids,
      (x) => {
        const n = { ...x, [k]: round1(v) };
        // Typed sizes obey the same floor as dragging a handle.
        const [mw, mh] = minSizeFor(x);
        if (k === "w") n.w = Math.max(n.w, mw);
        if (k === "h") n.h = Math.max(n.h, mh);
        // The QR stays square when sized by number too.
        if (ELEMENT_TYPES[x.type]?.keepRatio && (k === "w" || k === "h")) {
          n.w = Math.max(round1(v), mw);
          n.h = Math.max(round1(v), mh);
        }
        return n;
      },
      `geo-${k}-${ids.join()}`,
    );
  const s = el.style;
  const hasText = all((x) => TEXT_TYPES.has(x.type));
  const boxy = all((x) => x.type !== "line");

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <Typography noWrap sx={{ flex: 1, fontWeight: 700, fontSize: "0.82rem", color: INK }}>
          {single ? elementTitle(el) : `${selected.length} elements`}
        </Typography>
        <Tooltip title={el.hidden ? "Show" : "Hide (won't print)"}>
          <IconButton size="small" onClick={() => onUpdate(ids, (x) => ({ ...x, hidden: !el.hidden }))}>
            {el.hidden ? <VisibilityOffRounded fontSize="small" /> : <VisibilityRounded fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title={el.locked ? "Unlock" : "Lock position & size"}>
          <IconButton size="small" onClick={() => onUpdate(ids, (x) => ({ ...x, locked: !el.locked }))}>
            {el.locked ? <LockRounded fontSize="small" sx={{ color: BLUE }} /> : <LockOpenRounded fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton size="small" onClick={onDelete}>
            <DeleteRounded fontSize="small" sx={{ color: "#DC2626" }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Which face of the sheet it prints on. Moving it also turns the editor
          to that face (onMoveToSide), so the element never just vanishes. */}
      {onMoveToSide && (
        <Box sx={{ ...groupSx, display: "flex", alignItems: "center", gap: 1, py: 1 }}>
          <Typography sx={{ ...microLabelSx, mb: 0, flexShrink: 0 }}>Prints on</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={all((x) => x.side === el.side) ? el.side : null}
            onChange={(e, v) => v && onMoveToSide(ids, v)}
            sx={{ flex: 1, "& .MuiToggleButton-root": { flex: 1, py: 0.4, fontSize: "0.72rem", textTransform: "none" } }}
          >
            <ToggleButton value="front">Front side</ToggleButton>
            <ToggleButton value="back">Back side (reverse)</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Position & size */}
      {single && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>Position & Size (mm)</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <NumField label="X" value={el.x} onChange={(v) => setGeo("x", v)} />
            <NumField label="Y" value={el.y} onChange={(v) => setGeo("y", v)} />
            <NumField label="Width" value={el.w} min={0.5} onChange={(v) => setGeo("w", v)} />
            <NumField label="Height" value={el.h} min={0.3} onChange={(v) => setGeo("h", v)} />
            <NumField label="Rotation" value={el.rotation || 0} step={1} min={-180} max={180} suffix="°" onChange={(v) => setGeo("rotation", v)} />
          </Box>
        </Box>
      )}

      {/* Content */}
      {single && el.type === "field" && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>Data</Typography>
          <TextField select fullWidth size="small" label="Field" value={el.props.key} onChange={(e) => setProp("key", e.target.value)} sx={{ ...smallField, mb: 1 }}>
            {FIELD_GROUPS.flatMap((g) => [
              <ListSubheader key={`h-${g}`} sx={{ fontSize: "0.65rem", lineHeight: "26px" }}>{g}</ListSubheader>,
              ...FIELD_DEFS.filter((f) => f.group === g).map((f) => (
                <MenuItem key={f.key} value={f.key} sx={{ fontSize: "0.78rem" }}>{f.label}</MenuItem>
              )),
            ])}
          </TextField>
          <FormControlLabel
            control={<Switch size="small" checked={el.props.showLabel !== false} onChange={(e) => setProp("showLabel", e.target.checked)} />}
            label={<Typography sx={{ fontSize: "0.74rem" }}>Show label</Typography>}
          />
          {el.props.showLabel !== false && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 70px", gap: 1, mt: 0.5 }}>
              <TextField size="small" label="Label text (optional)" placeholder="Any language" value={el.props.label || ""} onChange={(e) => setProp("label", e.target.value, true)} sx={smallField} />
              <TextField size="small" label="Separator" value={el.props.labelSep ?? ": "} onChange={(e) => setProp("labelSep", e.target.value, true)} sx={smallField} />
              <FormControlLabel
                sx={{ gridColumn: "1 / -1" }}
                control={<Switch size="small" checked={el.props.labelBold !== false} onChange={(e) => setProp("labelBold", e.target.checked)} />}
                label={<Typography sx={{ fontSize: "0.74rem" }}>Bold label</Typography>}
              />
            </Box>
          )}
        </Box>
      )}

      {single && el.type === "text" && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>Text</Typography>
          <TextField
            inputRef={textRef}
            fullWidth
            multiline
            minRows={3}
            size="small"
            value={el.props.text || ""}
            onChange={(e) => setProp("text", e.target.value, true)}
            sx={{ ...smallField, mb: 1 }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Insert a data field"
            value=""
            onChange={(e) => setProp("text", `${el.props.text || ""}{${e.target.value}}`)}
            sx={smallField}
            helperText="Fields are written as {fieldName} and filled in when printing."
          >
            {FIELD_DEFS.map((f) => (
              <MenuItem key={f.key} value={f.key} sx={{ fontSize: "0.78rem" }}>
                {f.group} · {f.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {single && el.type === "block" && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>Block</Typography>
          <TextField select fullWidth size="small" value={el.props.section} onChange={(e) => setProp("section", e.target.value)} sx={smallField}
            helperText="The ornaments table and receipt amounts swap automatically to match the ticket type. Content too big for the box shrinks to fit — make the box bigger if the text gets too small.">
            {BLOCK_DEFS.map((b) => (
              <MenuItem key={b.id} value={b.id} sx={{ fontSize: "0.78rem" }}>{b.label}</MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      {single && el.type === "qr" && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>QR Code</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            <TextField select size="small" label="Error correction" value={el.props.level || "L"} onChange={(e) => setProp("level", e.target.value)} sx={smallField}>
              <MenuItem value="L">Low (densest fit)</MenuItem>
              <MenuItem value="M">Medium</MenuItem>
              <MenuItem value="Q">High</MenuItem>
            </TextField>
            <NumField label="Quiet zone" value={el.props.quietZone ?? 2} step={1} min={0} max={8} onChange={(v) => setProp("quietZone", v)} />
          </Box>
          <Typography sx={{ fontSize: "0.65rem", color: INK_MUTED, mt: 0.75 }}>
            Keep it at least 20mm wide so phones scan it reliably.
          </Typography>
        </Box>
      )}

      {single && (el.type === "photo" || el.type === "logo" || el.type === "image") && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>Picture</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            {el.type === "photo" && (
              <TextField select size="small" label="Photo" value={el.props.which || "customer"} onChange={(e) => setProp("which", e.target.value)} sx={smallField}>
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="ornament">Ornament</MenuItem>
              </TextField>
            )}
            <TextField select size="small" label="Fit" value={el.props.fit || "contain"} onChange={(e) => setProp("fit", e.target.value)} sx={smallField}>
              <MenuItem value="contain">Fit inside</MenuItem>
              <MenuItem value="cover">Fill & crop</MenuItem>
              <MenuItem value="fill">Stretch</MenuItem>
            </TextField>
          </Box>
          {el.type === "image" && (
            <Button
              component="label"
              size="small"
              variant="outlined"
              startIcon={<AddPhotoAlternateRounded />}
              sx={{ mt: 1, textTransform: "none", fontSize: "0.74rem" }}
            >
              {el.props.src ? "Replace image" : "Upload image"}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  const { url, ratio } = await readImage(file);
                  // Match the box to the picture's shape on first upload.
                  onUpdate(ids, (x) => ({
                    ...x,
                    h: x.props.src ? x.h : round1(x.w / (ratio || 1)),
                    props: { ...x.props, src: url },
                  }));
                }}
              />
            </Button>
          )}
        </Box>
      )}

      {single && el.type === "line" && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>Line</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
            <NumField label="Thickness (px)" value={el.props.thickness || 1} step={0.5} min={0.5} max={12} onChange={(v) => setProp("thickness", v)} />
            <TextField select size="small" label="Style" value={el.props.dash || "solid"} onChange={(e) => setProp("dash", e.target.value)} sx={smallField}>
              <MenuItem value="solid">Solid</MenuItem>
              <MenuItem value="dashed">Dashed</MenuItem>
              <MenuItem value="dotted">Dotted</MenuItem>
              <MenuItem value="double">Double</MenuItem>
            </TextField>
          </Box>
          <ColorField label="Line colour" value={s.color} onChange={(v) => setStyle("color", v, true)} />
        </Box>
      )}

      {/* Text style */}
      {hasText && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>Text</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Slider
              size="small"
              min={5}
              max={40}
              step={0.5}
              value={s.fontSize || 9}
              onChange={(e, v) => setStyle("fontSize", v, true)}
              sx={{ flex: 1 }}
            />
            <Box sx={{ width: 78 }}>
              <NumField label="Size" suffix="pt" value={s.fontSize || 9} min={4} max={96} onChange={(v) => setStyle("fontSize", v, true)} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
            <ToggleButtonGroup size="small">
              <ToggleButton value="b" selected={Number(s.fontWeight) >= 600} onChange={() => setStyle("fontWeight", Number(s.fontWeight) >= 600 ? 400 : 700)}>
                <FormatBoldRounded sx={{ fontSize: 16 }} />
              </ToggleButton>
              <ToggleButton value="i" selected={s.fontStyle === "italic"} onChange={() => setStyle("fontStyle", s.fontStyle === "italic" ? "normal" : "italic")}>
                <FormatItalicRounded sx={{ fontSize: 16 }} />
              </ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={s.align} onChange={(e, v) => v && setStyle("align", v)}>
              <ToggleButton value="left"><FormatAlignLeftRounded sx={{ fontSize: 16 }} /></ToggleButton>
              <ToggleButton value="center"><FormatAlignCenterRounded sx={{ fontSize: 16 }} /></ToggleButton>
              <ToggleButton value="right"><FormatAlignRightRounded sx={{ fontSize: 16 }} /></ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={s.vAlign} onChange={(e, v) => v && setStyle("vAlign", v)}>
              <ToggleButton value="top"><VerticalAlignTopRounded sx={{ fontSize: 16 }} /></ToggleButton>
              <ToggleButton value="middle"><VerticalAlignCenterRounded sx={{ fontSize: 16 }} /></ToggleButton>
              <ToggleButton value="bottom"><VerticalAlignBottomRounded sx={{ fontSize: 16 }} /></ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
            <TextField select size="small" label="Font" value={s.fontFamily || ""} onChange={(e) => setStyle("fontFamily", e.target.value)} sx={smallField}>
              {FONT_FAMILIES.map((f) => (
                <MenuItem key={f.label} value={f.value} sx={{ fontSize: "0.78rem", fontFamily: f.value || undefined }}>{f.label}</MenuItem>
              ))}
            </TextField>
            <NumField label="Line height" value={s.lineHeight || 1.3} step={0.05} min={0.8} max={3} onChange={(v) => setStyle("lineHeight", v, true)} />
          </Box>
          <ColorField label="Text colour" value={s.color} onChange={(v) => setStyle("color", v, true)} />
        </Box>
      )}

      {/* Box style */}
      {boxy && (
        <Box sx={groupSx}>
          <Typography sx={microLabelSx}>Box</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 1 }}>
            <NumField label="Border (px)" value={s.borderWidth || 0} step={0.5} min={0} max={10} onChange={(v) => setStyle("borderWidth", v, true)} />
            <TextField select size="small" label="Border style" value={s.borderStyle || "solid"} onChange={(e) => setStyle("borderStyle", e.target.value)} sx={smallField}>
              <MenuItem value="solid">Solid</MenuItem>
              <MenuItem value="dashed">Dashed</MenuItem>
              <MenuItem value="dotted">Dotted</MenuItem>
              <MenuItem value="double">Double</MenuItem>
            </TextField>
            <NumField label="Corner (mm)" value={s.radius || 0} step={0.5} min={0} max={20} onChange={(v) => setStyle("radius", v, true)} />
            <NumField label="Padding (mm)" value={s.padding ?? 0.5} step={0.5} min={0} max={20} onChange={(v) => setStyle("padding", v, true)} />
          </Box>
          <Box sx={{ display: "grid", gap: 0.75, mb: 1 }}>
            <ColorField label="Border colour" value={s.borderColor} onChange={(v) => setStyle("borderColor", v, true)} />
            <ColorField label="Background" value={s.bg} allowNone onChange={(v) => setStyle("bg", v, true)} />
          </Box>
          <Typography sx={{ fontSize: "0.68rem", color: INK_MUTED }}>Opacity</Typography>
          <Slider size="small" min={0.1} max={1} step={0.05} value={s.opacity ?? 1} onChange={(e, v) => setStyle("opacity", v, true)} />
        </Box>
      )}
    </Box>
  );
};
